import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell, PageTitle } from "@/components/AppShell";
import { downloadBackup } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ChangePassword } from "@/components/ChangePassword";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const Tools = ({ shop, onChange }) => (
  <section data-testid="demo-tester-tools" className="rounded-2xl border-2 border-dashed border-[#E9C98B]/40 bg-[#E9C98B]/[0.05] p-6">
    <h2 className="text-xl font-medium">Demo tester tools</h2>
    <p className="mt-1 text-sm text-muted-foreground">Force failures to check how AetherTags behaves. Only available in Demo Mode.</p>
    {[["simulate_ai_failure", "Simulate AI failure"], ["simulate_push_failure", "Simulate push failure"]].map(([k, l]) => (
      <label key={k} className="mt-4 flex items-center justify-between gap-4 text-sm">
        {l}
        <Switch checked={shop[k]} onCheckedChange={(v) => onChange({ [k]: v })} data-testid={`tool-${k.replace(/_/g, "-")}`} />
      </label>
    ))}
  </section>
);

export default function Settings() {
  const { shop, loadShop } = useAuth();
  const navigate = useNavigate();
  const disconnect = async () => {
    try { await api.delete("/shop"); await loadShop(); toast.success("Shop disconnected. Stored tokens and shop data deleted."); navigate("/app"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const tools = async (patch) => {
    try { await api.patch("/shop/demo-tools", patch); await loadShop(); } catch (e) { toast.error(errMsg(e)); }
  };
  const sync = async () => {
    try { await api.post("/shop/sync"); await loadShop(); toast.success("Synced with Etsy."); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AppShell>
      <PageTitle eyebrow="Settings" title="Shop & data" testid="settings-title" />
      <div className="grid gap-6 lg:grid-cols-2">
        <section data-testid="settings-shop" className="rounded-2xl border glass p-6">
          <h2 className="text-xl font-medium">Connected shop</h2>
          {shop ? (
            <>
              <p data-testid="settings-shop-name" className="mt-3 font-display text-2xl">{shop.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{shop.kind === "demo" ? "Demo Mode (simulated data)" : "Etsy shop via official OAuth"} · {shop.listing_count} listings</p>
              {shop.last_synced_at && <p className="mt-1 font-mono text-xs text-muted-foreground">Last synced {format(new Date(shop.last_synced_at), "MMM d, HH:mm")}{shop.kind === "etsy" ? " · refreshed at least every 6 hours" : ""}</p>}
              <div className="mt-6 flex flex-wrap gap-2">
                <Button variant="outline" className="rounded-full" onClick={downloadBackup} data-testid="settings-download-backup">Download backup</Button>
                {shop.kind === "etsy" && <Button variant="outline" className="rounded-full" onClick={sync} data-testid="settings-sync-button">Sync now</Button>}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="rounded-full" data-testid="disconnect-shop-button">Disconnect shop</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="disconnect-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect {shop.name}?</AlertDialogTitle>
                      <AlertDialogDescription>We delete the stored Etsy tokens plus this shop's listings, proposals and version history from AetherTags. Your Etsy shop itself is not changed. Download a backup first if you want one.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="disconnect-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={disconnect} data-testid="disconnect-confirm">Disconnect</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </>
          ) : <p className="mt-3 text-sm text-muted-foreground">No shop connected.</p>}
        </section>
        <section className="rounded-2xl border glass p-6 text-sm text-muted-foreground leading-relaxed space-y-3" data-testid="settings-data-notes">
          <h2 className="text-xl font-medium text-foreground">How we handle your data</h2>
          <p>We use Etsy's official API only. No scraping. Nothing is pushed to your shop without your "Approve & push" click.</p>
          <p>AI fixes are written by Claude Opus 5.5 through a provider that does not train on your content. Your Etsy content is never used to train AI models.</p>
          <p>You're responsible for listing accuracy, including Etsy's disclosure rules for items made with AI.</p>
        </section>
        {shop?.kind === "demo" && <Tools shop={shop} onChange={tools} />}
        <div id="change-password"><ChangePassword /></div>
      </div>
    </AppShell>
  );
}
